import * as React from 'react'
import { getPreviousOdometerEnd } from '@/lib/api/company/chartScan'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list/useQueryVehiclesList'
import { SearchSelect } from './SearchSelect'
import { fmt, useChartT } from '../i18n'

// 1:1 with WPF TachographChartChangeVehicleDialog: change time, the vehicle and that
// vehicle's own odometer start/end, with the chart read-out for its part of the day.
type Props = {
  companyId: string
  atIso: string
  // Editing the first vehicle of the day: its time is the day start (WPF IsNotFirstVehicleChange = false).
  timeLocked?: boolean
  initial?: { vehicleId?: number | null; odometerStart: number; odometerEnd: number }
  dayStartIso: string
  dayEndIso: string
  // Odometer the vehicle ended on earlier on this chart, if it was used before `time`.
  lastOdometerOf: (vehicleId: number, time: string) => number | null
  // Chart read-out for a vehicle starting at `time`; null when the disc has no distance points.
  chartDistance: (time: string) => number | null
  onConfirm: (v: { time: string; vehicleId: number | null; odometerStart: number; odometerEnd: number }) => void
  onCancel: () => void
}

const inputClass = 'tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm'
const labelClass = 'tw-text-xs tw-font-medium tw-text-slate-700'
const readOnlyClass = 'tw-px-2 tw-py-1 tw-text-sm'

export function VehicleChangeDialog({
  companyId,
  atIso,
  timeLocked = false,
  initial,
  dayStartIso,
  dayEndIso,
  lastOdometerOf,
  chartDistance,
  onConfirm,
  onCancel,
}: Props) {
  const T = useChartT()
  const [time, setTime] = React.useState(atIso.slice(11, 16))
  const [vehicleId, setVehicleId] = React.useState<number | null>(initial?.vehicleId ?? null)
  const [odometerStart, setOdometerStart] = React.useState(initial?.odometerStart ?? 0)
  const [odometerEnd, setOdometerEnd] = React.useState(initial?.odometerEnd ?? 0)
  // WPF _minimalStartOdometer: where this vehicle ended earlier on the chart.
  const [minimalStart, setMinimalStart] = React.useState(() =>
    initial?.vehicleId != null && !timeLocked ? (lastOdometerOf(initial.vehicleId, atIso.slice(11, 16)) ?? 0) : 0,
  )
  const pickedVehicle = React.useRef(vehicleId)
  const timeId = React.useId()
  const odometerEndId = React.useId()
  const odometerStartId = React.useId()

  const dayStart = dayStartIso.slice(11, 16)
  const dayEnd = dayEndIso.slice(11, 16)
  const timeInDay =
    timeLocked ||
    (/^\d{2}:\d{2}$/.test(time) &&
      (dayStart < dayEnd ? time > dayStart && time < dayEnd : time > dayStart || time < dayEnd))
  const readOut = timeInDay ? chartDistance(time) : null

  // WPF RefreshOdometerValues: continue from where the vehicle ended — earlier on this
  // chart, otherwise on its last use before the change.
  const pickVehicle = (id: number | null) => {
    setVehicleId(id)
    pickedVehicle.current = id
    setMinimalStart(0)
    if (id == null) return
    const apply = (previous: number) => {
      setOdometerStart(previous)
      setOdometerEnd((end) => (end === 0 ? previous : end))
    }
    const onChart = timeLocked ? null : lastOdometerOf(id, time)
    if (onChart != null) {
      setMinimalStart(onChart)
      apply(onChart)
      return
    }
    getPreviousOdometerEnd(companyId, id, `${dayStartIso.slice(0, 10)}T${time}:00`)
      .then((r) => {
        if (pickedVehicle.current === id && r.data.odometerEnd != null) apply(r.data.odometerEnd)
      })
      .catch(() => undefined)
  }

  const useSearch = (search: string) => {
    const q = useQueryVehiclesList({ companyId, search, pageSize: 25 })
    const list = (q.data as any) || []
    const vehicles = Array.isArray(list) ? list : list.vehicles || []
    return {
      items: vehicles.map((v: any) => ({
        id: v.id,
        label: v.registrationNumber || v.registrationNumberFromTachograph || `#${v.id}`,
      })),
      isLoading: q.isLoading,
    }
  }

  const odometerWarning =
    minimalStart > odometerStart
      ? T('odometerStartBelowPrevious', 'Licznik początkowy nie może być mniejszy niż poprzednia wartość dla danego pojazdu')
      : odometerEnd < odometerStart
        ? T('odometerEndBelowStart', 'Licznik końcowy musi być większy od początkowego')
        : null

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[10001] tw-flex tw-items-center tw-justify-center tw-bg-black/50"
      onMouseDown={onCancel}
    >
      <div
        className="tw-w-[460px] tw-rounded tw-bg-white tw-p-4 tw-shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="tw-mb-3 tw-text-lg tw-font-semibold">
          {T('vehicleChange', 'Zmiana pojazdu')}
        </h3>
        <div className="tw-mb-3">
          <label htmlFor={timeId} className={`${labelClass} tw-mb-1 tw-block`}>
            {T('changeTime', 'Czas zmiany:')}
          </label>
          <input
            id={timeId}
            type="time"
            value={time}
            disabled={timeLocked}
            onChange={(e) => setTime(e.target.value)}
            className={`${inputClass} tw-w-32 tw-font-mono disabled:tw-bg-slate-100`}
          />
          {!timeInDay && (
            <div className="tw-mt-1 tw-text-xs tw-text-red-600">
              {fmt(
                T('vehicleChangeTimeOutOfDay', 'Czas zmiany musi mieścić się w dniu pracy ({0}–{1}).'),
                dayStart,
                dayEnd,
              )}
            </div>
          )}
        </div>
        <SearchSelect
          menuZIndex={10002}
          label={T('newVehicle', 'Nowy pojazd')}
          placeholder={T('searchVehicle', 'Wyszukaj pojazd...')}
          value={vehicleId}
          onChange={pickVehicle}
          useSearch={useSearch}
        />
        <div className="tw-mt-3 tw-grid tw-grid-cols-4 tw-gap-x-2 tw-gap-y-1">
          <label htmlFor={odometerEndId} className={labelClass}>
            {T('odometerEnd', 'Koniec (km)')}
          </label>
          <label htmlFor={odometerStartId} className={labelClass}>
            {T('odometerStart', 'Start (km)')}
          </label>
          <span className={labelClass}>{T('distance', 'Przebieg (km)')}</span>
          <span className={labelClass}>{T('distanceFromChart', 'Z tarczki (km)')}</span>
          <input
            id={odometerEndId}
            type="number"
            min={0}
            value={odometerEnd}
            onChange={(e) => setOdometerEnd(parseInt(e.target.value || '0', 10))}
            className={inputClass}
          />
          <input
            id={odometerStartId}
            type="number"
            min={0}
            value={odometerStart}
            onChange={(e) => setOdometerStart(parseInt(e.target.value || '0', 10))}
            className={inputClass}
          />
          <span className={readOnlyClass}>{Math.max(0, odometerEnd - odometerStart)}</span>
          <span className={readOnlyClass}>{readOut ?? '–'}</span>
        </div>
        {odometerWarning && <div className="tw-mt-1 tw-text-xs tw-text-amber-600">{odometerWarning}</div>}
        <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
          <button
            onClick={onCancel}
            className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1.5 tw-text-sm"
          >
            {T('cancel', 'Anuluj')}
          </button>
          <button
            disabled={vehicleId == null || !timeInDay}
            onClick={() => onConfirm({ time, vehicleId, odometerStart, odometerEnd })}
            className="tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}
